import { Component, OnInit, inject } from '@angular/core';
import { RouterLink } from '@angular/router';
import { FormsModule } from '@angular/forms';
import { PageTitleService } from '../../core/services/page-title.service';
import { PasswordResetService } from '../../core/services/password-reset.service';
import { StudioService } from '../../core/services/studio.service';
import { FlashService } from '../../core/services/flash.service';
import { FlashMessageComponent } from '../../shared/components/flash-message.component';

@Component({
    selector: 'app-password-reset-execute',
    imports: [RouterLink, FormsModule, FlashMessageComponent],
    template: `
<div class="animated fadeIn">
  <div class="row sfapp-container">
    <div class="col-md-12">
      <div class="card">
        <div class="card-header">
          <span><i class="fa s-icon-edit"></i>&nbsp;パスワード付与</span>
        </div>
        <div class="card-body">
          <div class="col-md-12">
            <!-- 旧版 password-reset-execute.html:11-13 の col-md-12 先頭 <flash-message> 相当 -->
            <div class="row"><app-flash-message class="col-md-12"></app-flash-message></div>
            <form novalidate name="passwordResetExecForm">
              <div class="row">
                <div class="col-md-12">
                  <label class="col-form-label">実行店舗</label>
                  <div class="form-group col-md-6 border" style="max-height: 300px; overflow-y: auto;">
                    <table class="table table-sm">
                      @for (studio of studios; track studio.id; let i = $index) {
                        <tr>
                          <td class="border-0">
                            <label class="mb-0 w-100">
                              <div class="custom-control custom-checkbox custom-control-inline">
                                <input class="custom-control-input" [(ngModel)]="studioCheck[studio.id]" [id]="'check_' + i" type="checkbox" [value]="studio.id" [name]="'check_' + i" (ngModelChange)="countCheck()">
                                <label class="custom-control-label" [for]="'check_' + i">{{studio.name}}</label>
                              </div>
                            </label>
                          </td>
                        </tr>
                      }
                    </table>
                    <input type="hidden" name="isChecked" [ngModel]="isChecked" pattern="true">
                  </div>
                </div>
              </div>
            </form>
            <div class="row">
              <div class="col-md-12 text-right">
                <a routerLink="/password-reset" class="btn bg-close text-white"><i class="fa s-icon-cancel"></i>&nbsp;閉じる</a>
                <button type="button" class="btn btn-success" (click)="openModal()" [disabled]="disableExecution || !anyChecked()">
                  <i class="fa s-icon-passward"></i>&nbsp;実行
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</div>

@if (showModal) {
  <div class="modal-backdrop fade show"></div>
  <div class="modal fade show d-block" id="passwordResetModal" tabindex="-1" role="dialog" aria-labelledby="passwordResetModalModalLabel">
    <div class="modal-dialog" role="document">
      <div class="modal-content">
        <div class="modal-header">
          <h5 class="modal-title" id="passwordResetModalModalLabel">パスワード付与</h5>
          <button type="button" class="close" (click)="showModal = false">
            <span class="text-white" aria-hidden="true">&times;</span>
          </button>
        </div>
        <form novalidate name="passwordResetForm">
        <div class="modal-body">
          <div class="form-group row">
            <label class="col-md-12 col-form-label">下記店舗のパスワードが初期状態の会員に対して、パスワードを一括付与します。よろしいですか？</label>
          </div>
          <div class="form-group row">
            <label class="col-md-12 col-form-label">パスワード付与実行店舗</label>
            <div class="col-md-12" style="max-height: 300px; overflow-y: auto;">
              @for (item of selectItems; track $index) {
                <ul class="col-md-12">
                  <li class="list-unstyled">{{item.name}}</li>
                </ul>
              }
            </div>
          </div>
        </div>
        <div class="modal-footer">
          <button class="btn bg-close text-white" type="button" (click)="showModal = false"><i class="fa s-icon-cancel"></i>&nbsp;閉じる</button>
          <button class="btn btn-success" type="submit" (click)="reset()" [disabled]="disableExecution"><i class="fa s-icon-passward"></i>&nbsp;実行</button>
        </div>
        </form>
      </div>
    </div>
  </div>
}
  `
})
export class PasswordResetExecuteComponent implements OnInit {
  private pageTitleService = inject(PageTitleService);
  private passwordResetService = inject(PasswordResetService);
  private studioService = inject(StudioService);
  private flash = inject(FlashService);

  studios: any[] = [];
  studioCheck: { [id: number]: boolean } = {};
  disableExecution = true;
  showModal = false;
  selectItems: { name: string }[] = [];
  isChecked = false;

  async ngOnInit(): Promise<void> {
    this.pageTitleService.set('パスワード付与', 's-icon-passward');
    this.studios = await this.studioService.list('all') ?? [];
    await this.updateDisableExecution();
  }

  anyChecked(): boolean {
    return Object.values(this.studioCheck).some(v => v);
  }

  countCheck(): void {
    this.isChecked = this.anyChecked();
  }

  private getCheckIds(): number[] {
    return Object.entries(this.studioCheck)
      .filter(([, v]) => v)
      .map(([k]) => Number(k));
  }

  openModal(): void {
    const checkIds = this.getCheckIds();
    this.selectItems = this.studios
      .filter(s => checkIds.includes(s.id))
      .map(s => ({ name: s.name }));
    this.showModal = true;
  }

  async reset(): Promise<void> {
    await this.updateDisableExecution();
    const json = { studioIds: this.getCheckIds() };
    await this.passwordResetService.add(json);
    // 旧版 password-reset.js:122-125: 登録完了時に Flash（30秒）
    this.flash.create('success', 'パスワードリセットを開始しました。しばらくたってから一覧画面を更新して結果を確認してください。', 30000);
    await this.updateDisableExecution();
    await this.passwordResetService.allocation(json);
    this.showModal = false;
  }

  private async updateDisableExecution(): Promise<void> {
    const data: any[] = await this.passwordResetService.list() ?? [];
    this.disableExecution = data.some((v: any) => v.status !== 2 && v.status !== 3);
  }
}
